import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { FieldShell } from "@cntrl/ui";
import styles from "./TypeaheadInput.module.scss";

/** Debounce a value: the input settles for `ms` before it becomes the query the autocomplete hook fetches on
 * (~275ms — the vendor and its server cache sit behind this, so keystrokes don't fan out).
 *
 * `ms <= 0` passes the value straight through — no timer, no extra render. A debounce only buys something
 * when the query COSTS something; over a list already in memory it is latency the caller pays for nothing. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (ms <= 0) return;
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return ms <= 0 ? value : debounced;
}

export interface TypeaheadSuggestions<T> {
  items: T[];
  /** false = the provider is DEGRADED → render a plain-text input, never an error (R5). */
  available: boolean;
}

export interface TypeaheadInputProps<T> {
  /** Visible mono-uppercase label; omit for a bare, aria-labelled input (e.g. a lineup row cell). */
  label?: string;
  /** Accessible name when there is no visible `label`. */
  ariaLabel?: string;
  placeholder?: string;
  /** The denormalized free-text value the form stores. */
  value: string;
  /** Fires on every keystroke AND on pick — the show form persists this text (never an id). */
  onChange: (text: string) => void;
  /** Fires only when a suggestion is picked — lets the caller fill sibling fields / capture an external id. */
  onPick?: (item: T) => void;
  /** The autocomplete source — a hook called with the DEBOUNCED query (Rules of Hooks: called every render). */
  useSuggestions: (q: string) => TypeaheadSuggestions<T>;
  /** The text a picked item commits to `onChange`. */
  itemText: (item: T) => string;
  /** A stable list key. */
  itemKey: (item: T, index: number) => string;
  /** The option's primary line (e.g. the name). */
  itemPrimary: (item: T) => ReactNode;
  /** An optional secondary line (e.g. "Los Angeles, CA" or a source badge). */
  itemSecondary?: (item: T) => ReactNode;
  /** Override the input class (a lineup cell reuses the wizard's `.slotArtist`). */
  inputClassName?: string;
  /**
   * Characters before the dropdown may open. Defaults to 2 — the right floor for a VENDOR-backed source,
   * where a one-letter query is a wasted round trip and a useless list.
   *
   * Pass 0 for a source already in memory (the setlist's Song Bank): there the list is also a BROWSE — "what
   * songs do I have?" is a real question, and a two-character floor makes it unanswerable.
   */
  minLength?: number;
  /**
   * Milliseconds the input must settle before the query reaches {@link useSuggestions}. Defaults to 275 —
   * right for a VENDOR-backed source, where every keystroke would otherwise be a round trip.
   *
   * Pass 0 for an in-memory source: there the debounce buys nothing and the list visibly lags the caret.
   */
  debounceMs?: number;
  /**
   * A non-selectable line under the suggestions — a vendor's required attribution (Google Places must carry the
   * Google Maps mark; Spotify results must name Spotify). Rendered only while the list is, so it never shows on
   * a plain-text degrade.
   */
  footer?: ReactNode;
}

const DEBOUNCE_MS = 275;
const DEFAULT_MIN_LENGTH = 2;

/**
 * A degrade-first typeahead: a text input plus a keyboard-navigable suggestion dropdown that appears
 * ONLY when the provider is available and the query is ≥ 2 chars. When `available` is false (vendor degraded)
 * or the query is short, it is exactly a plain-text input — no dropdown, no error. Picking a suggestion (click,
 * or ↑/↓ + Enter) commits its text via `onChange` and fires `onPick`; the show form stores the text, not an id.
 */
export function TypeaheadInput<T>({
  label,
  ariaLabel,
  placeholder,
  value,
  onChange,
  onPick,
  useSuggestions,
  itemText,
  itemKey,
  itemPrimary,
  itemSecondary,
  inputClassName,
  minLength = DEFAULT_MIN_LENGTH,
  debounceMs = DEBOUNCE_MS,
  footer,
}: TypeaheadInputProps<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debounced = useDebounced(value, debounceMs);
  const { items, available } = useSuggestions(debounced);
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on an outside click (a pick uses onMouseDown so it lands before the input blurs).
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const showList = open && available && items.length > 0 && value.trim().length >= minLength;

  function commit(item: T) {
    onChange(itemText(item));
    onPick?.(item);
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!showList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      commit(items[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
    }
  }

  // The input, minus its frame. `className` comes from FieldShell in the labelled case — that is the class
  // carrying `--ac-control-h`, and taking it is the whole point of routing through the shell.
  const input = (className: string, props?: { id?: string; "aria-describedby"?: string }) => (
    <input
      {...props}
      className={inputClassName ?? className}
      type="text"
      role="combobox"
      aria-expanded={showList}
      aria-controls={showList ? listId : undefined}
      aria-autocomplete="list"
      aria-label={label ? undefined : ariaLabel}
      autoComplete="off"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        setOpen(true);
        setActive(-1);
      }}
      onFocus={() => setOpen(true)}
      onKeyDown={onKeyDown}
    />
  );

  const list = showList && (
    <ul className={styles.list} id={listId} role="listbox">
      {items.map((item, i) => (
        <li
          key={itemKey(item, i)}
          role="option"
          aria-selected={i === active}
          className={styles.option}
          data-active={i === active || undefined}
          onMouseDown={(e) => {
            e.preventDefault();
            commit(item);
          }}
          onMouseEnter={() => setActive(i)}
        >
          <span className={styles.optionPrimary}>{itemPrimary(item)}</span>
          {/* Render the meta line only when the callback returns something — a row that wants no sub-line
              (e.g. a catalog song) returns null and gets no empty gap. */}
          {(() => {
            const meta = itemSecondary?.(item);
            return meta ? <span className={styles.optionMeta}>{meta}</span> : null;
          })()}
        </li>
      ))}
      {footer != null && (
        <li role="none" className={styles.footer}>
          {footer}
        </li>
      )}
    </ul>
  );

  // LABELLED: the frame is `FieldShell` — the primitive that exists for "a control the typed components
  // don't cover (a combo box…)". It hands down the shared `.control` class, so the box inherits
  // `--ac-control-h` and `--ac-control-fs` and can never drift from the plain fields beside it again. This
  // component used to hand-roll both the label and the input, with a comment claiming they matched `Field`;
  // they did when it was written, and then the primitive moved (`--ac-control-h` landed and block padding
  // was deliberately removed), leaving the venue box ~44px tall in a row of 28px ones. Same failure the
  // Field stylesheet already records for `Picker`'s typeahead trigger — this was the third instance.
  if (label) {
    return (
      <FieldShell label={label} className={styles.root}>
        {({ id, "aria-describedby": describedBy, className }) => (
          <div ref={rootRef} className={styles.anchor}>
            {input(className, { id, "aria-describedby": describedBy })}
            {list}
          </div>
        )}
      </FieldShell>
    );
  }

  // BARE (a lineup row cell): no label, and the caller supplies the cell's own dense class.
  return (
    <div ref={rootRef} className={styles.anchor}>
      {input(styles.input)}
      {list}
    </div>
  );
}
